import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import CommunityLayout from '../../components/community/CommunityLayout'

function CommunityHome() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [profile, setProfile] = useState(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [selectedPost, setSelectedPost] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    fetchPosts()
    if (user) fetchProfile()
    
    // Realtime subscription
    const channel = supabase
      .channel('posts-channel')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts' }, handlePostChange)
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  const fetchPosts = async () => {
    try {
      const { data: postsData, error } = await supabase
        .from('posts')
        .select('*')
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error

      // Fetch authors separately
      const authorIds = [...new Set(postsData.map(p => p.author_id))]
      const { data: authorsData } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, role')
        .in('id', authorIds)

      const authorsMap = {}
      authorsData?.forEach(author => {
        authorsMap[author.id] = author
      })

      // Fetch reaction counts and comment counts
      const postsWithCounts = await Promise.all(
        postsData.map(async (post) => {
          const [{ count: reactionCount }, { count: commentCount }] = await Promise.all([
            supabase.from('reactions').select('*', { count: 'exact', head: true }).eq('post_id', post.id),
            supabase.from('comments').select('*', { count: 'exact', head: true }).eq('post_id', post.id),
          ])
          return { 
            ...post, 
            author: authorsMap[post.author_id],
            reactionCount: reactionCount || 0, 
            commentCount: commentCount || 0 
          }
        })
      )

      setPosts(postsWithCounts)
    } catch (error) {
      console.error('Error fetching posts:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchProfile = async () => {
    if (!user) return
    try {
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single()

      setProfile(data)
    } catch (error) {
      console.error('Error fetching profile:', error)
    }
  }

  const handlePostChange = (payload) => {
    if (payload.eventType === 'INSERT') {
      fetchPosts()
    } else if (payload.eventType === 'UPDATE') {
      setPosts(prev => prev.map(p => p.id === payload.new.id ? { ...p, ...payload.new } : p))
    } else if (payload.eventType === 'DELETE') {
      setPosts(prev => prev.filter(p => p.id !== payload.old.id))
    }
  }

  const handleMenuOpen = (event, post) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setSelectedPost(post)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setSelectedPost(null)
  }

  const handleDeletePost = async () => {
    if (!selectedPost) return
    
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('posts')
          .delete()
          .eq('id', selectedPost.id)

        if (error) throw error

        setPosts(prev => prev.filter(p => p.id !== selectedPost.id))
        setSnackbar({
          open: true,
          message: 'Post deleted successfully',
          severity: 'success',
        })
      } catch (error) {
        console.error('Error deleting post:', error)
        setSnackbar({
          open: true,
          message: 'Failed to delete post',
          severity: 'error',
        })
      }
    }
    handleMenuClose()
  }

  const handleHidePost = async () => {
    if (!selectedPost) return
    
    try {
      const { error } = await supabase
        .from('posts')
        .update({ is_hidden: true })
        .eq('id', selectedPost.id)

      if (error) throw error

      setPosts(prev => prev.filter(p => p.id !== selectedPost.id))
      setSnackbar({
        open: true,
        message: 'Post hidden successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error hiding post:', error)
      setSnackbar({
        open: true,
        message: 'Failed to hide post',
        severity: 'error',
      })
    }
    handleMenuClose()
  }

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.body.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return (
    <CommunityLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary' }}>
          Community Discussions
        </Typography>
        {user && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/community/new')}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            New Post
          </Button>
        )}
      </Box>

      <TextField
        fullWidth
        placeholder="Search discussions..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Stack spacing={2}>
          {filteredPosts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No posts yet. Be the first to start a discussion!
            </Typography>
          ) : (
            filteredPosts.map((post) => (
              <Card
                key={post.id}
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
                onClick={() => navigate(`/community/post/${post.id}`)}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      src={post.author?.avatar_url}
                      sx={{
                        width: 40,
                        height: 40,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      }}
                    >
                      {post.author?.full_name?.charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {post.author?.full_name}
                        </Typography>
                        {post.author?.role !== 'member' && (
                          <Chip
                            label={post.author?.role}
                            size="small"
                            color={post.author?.role === 'admin' ? 'error' : 'warning'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          · {formatDate(post.created_at)}
                        </Typography>
                      </Stack>

                      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                        {post.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {post.body}
                      </Typography>

                      <Stack direction="row" spacing={3}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <ThumbUpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {post.reactionCount}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CommentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {post.commentCount}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <ViewIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {post.view_count}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>

                    {/* Admin Controls */}
                    {(profile?.role === 'admin' || profile?.role === 'moderator' || user?.id === post.author_id) && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, post)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      )}

      {/* Admin Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {(profile?.role === 'admin' || profile?.role === 'moderator') && (
          <MenuItem onClick={handleHidePost}>
            <ListItemIcon>
              <HideIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Hide Post</ListItemText>
          </MenuItem>
        )}
        {(profile?.role === 'admin' || profile?.role === 'moderator' || user?.id === selectedPost?.author_id) && (
          <MenuItem onClick={handleDeletePost} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Post</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </CommunityLayout>
  )
}

export default CommunityHome